<script setup lang="ts">
import type { Values } from '@/composables/useValues'

/**
 * Use query params.
 */
const { config } = useParams()

/**
 * Use countries.
 */
const { find: findCountry } = useCountries()

/**
 * Use framework.
 */
const {
    countryFramework,
    isImportingRisk,
    isGovernmentResponse,
    isPrevalence,
    isVulnerability
} = useFramework()

/**
 * Current country.
 */
const country = computed(() => {
    return findCountry(config.country)
})

/**
 * List of dimensions for the given country.
 */
const dimensions = computed(() => {
    return unref(countryFramework).filter(item => {
        return !item.parent && (!isImportingRisk(item) || unref(country)?.g20)
    })
})

/**
 * Currently selected dimension.
 */
const dimension = computed(() => {
    return unref(dimensions).find(({ id }) => id === config.dimension)
})

/**
 * List of tabs.
 */
const tabs = computed(() => {
    return unref(dimensions).map(item => ({
        label: item.name,
        value: item.id
    }))
})

/**
 * Get current data.
 */
const data = reactive(useValues(dimension, true))

/**
 * Check and type if data is import data.
 */
function isImportData(
    dimension: Dimension,
    _data: unknown
): _data is Values<Import> {
    return Boolean(isImportingRisk(dimension) && unref(country)?.g20)
}

/**
 * Reset the country card when the dimension changes.
 */
watch(dimension, () => {
    config.countryCard = []
})
</script>
        
<template>
    <div class="flex min-h-full flex-col bg-neutral xl:h-full">
        <UiTabs
            v-model="config.dimension"
            class="flex-1 overflow-hidden"
            :items="tabs"
            :default-index="0"
            :loading="data.loading"
        >
            <template #extra>
                <slot />
            </template>

            <template v-if="dimension && country">
                <CountryTabContainer
                    :dimension="dimension"
                    :data="data.countryData"
                >
                    <CountryGovernmentResponse
                        v-if="isGovernmentResponse(dimension)"
                        :data="data"
                        :country="country"
                        :dimension="dimension"
                    />

                    <CountryVulnerability
                        v-else-if="isVulnerability(dimension)"
                        :data="data"
                        :country="country"
                        :dimension="dimension"
                    />

                    <CountryPrevalence
                        v-else-if="isPrevalence(dimension)"
                        :country="country"
                        :data="data"
                        :dimension="dimension"
                    />

                    <CountryImportingRisk
                        v-else-if="isImportData(dimension, data)"
                        :data="data"
                        :country="country"
                        :dimension="dimension"
                    />
                </CountryTabContainer>
            </template>
        </UiTabs>

        <CountryToolbar
            :data="data"
            :dimension="dimension"
            class="flex-shrink-0"
        />
    </div>
</template>